/**
 * Serviço de XP - Sistema modular de experiência e níveis
 * 
 * Este serviço gerencia:
 * - Cálculo de níveis baseado em XP
 * - Progresso para o próximo nível
 * - Pontuação ofensiva (reset semanal)
 * - Persistência de dados
 */

// Constantes de XP por ação (podem ser ajustadas)
export const XP_REWARDS = {
  WORKOUT_COMPLETED: 10,
  MEAL_LOGGED: 5,
  SLEEP_GOAL_MET: 5,
  CHALLENGE_COMPLETED: 20,
  CUSTOM_GOAL_COMPLETED: 15,
  LOAD_REGISTERED: 10,
} as const;

// Fórmula de XP necessária por nível (exponencial)
// Nível 1: 0 XP
// Nível 2: 100 XP
// Nível 3: 250 XP
// Nível 4: 450 XP
// etc...
function getXPForLevel(level: number): number {
  if (level <= 1) return 0;
  // Fórmula: 50 * (level - 1) * level
  return 50 * (level - 1) * level;
}

/**
 * Calcula o nível atual baseado no XP total
 * @param xpTotal - XP total do usuário
 * @returns Nível atual (mínimo 1)
 */
export function getLevel(xpTotal: number): number {
  if (xpTotal < 0) return 1;
  
  let level = 1;
  while (getXPForLevel(level + 1) <= xpTotal) {
    level++;
  }
  
  return level;
}

/**
 * Calcula o XP necessário para o próximo nível
 * @param currentLevel - Nível atual
 * @returns XP necessário para o próximo nível
 */
export function getXPForNextLevel(currentLevel: number): number {
  return getXPForLevel(currentLevel + 1);
}

/**
 * Calcula o progresso para o próximo nível (0-1)
 * @param xpTotal - XP total do usuário
 * @returns Progresso de 0 a 1 (0% a 100%)
 */
export function getProgressToNextLevel(xpTotal: number): number {
  const currentLevel = getLevel(xpTotal);
  const xpForCurrentLevel = getXPForLevel(currentLevel);
  const xpForNextLevel = getXPForNextLevel(currentLevel);
  const xpInCurrentLevel = xpTotal - xpForCurrentLevel;
  const xpNeededForNextLevel = xpForNextLevel - xpForCurrentLevel;
  
  if (xpNeededForNextLevel === 0) return 1;
  
  return Math.min(1, Math.max(0, xpInCurrentLevel / xpNeededForNextLevel));
}

/**
 * Calcula o XP necessário para alcançar o próximo nível
 * @param xpTotal - XP total do usuário
 * @returns XP necessário para o próximo nível
 */
export function getXPNeededForNextLevel(xpTotal: number): number {
  const currentLevel = getLevel(xpTotal);
  const xpForCurrentLevel = getXPForLevel(currentLevel);
  const xpForNextLevel = getXPForNextLevel(currentLevel);
  const xpInCurrentLevel = xpTotal - xpForCurrentLevel;
  const xpNeeded = xpForNextLevel - xpForCurrentLevel - xpInCurrentLevel;
  
  return Math.max(0, xpNeeded);
}

/**
 * Obtém informações completas do nível atual
 */
export function getLevelInfo(xpTotal: number) {
  const level = getLevel(xpTotal);
  const xpForCurrentLevel = getXPForLevel(level);
  const xpForNextLevel = getXPForNextLevel(level);
  const xpInCurrentLevel = xpTotal - xpForCurrentLevel;
  const progress = getProgressToNextLevel(xpTotal);
  const xpNeeded = getXPNeededForNextLevel(xpTotal);
  
  return {
    level,
    xpTotal,
    xpForCurrentLevel,
    xpForNextLevel,
    xpInCurrentLevel,
    xpNeeded,
    progress,
  };
}

